import { Link } from 'react-router-dom';
import { mockTopics } from '@/mocks/topics';
import StanceBar from '@/components/feature/StanceBar';
import { categoryName } from '@/components/feature/TopicCard';

function formatHeat(heat: number) {
  return heat >= 10000 ? `${(heat / 10000).toFixed(1)}万` : `${heat}`;
}

export default function TrendingBoard() {
  const sorted = [...mockTopics].sort((a, b) => b.heat - a.heat);
  const featured = sorted.find((t) => t.editorsPick) ?? sorted[0];
  const rest = sorted.filter((t) => t.id !== featured.id).slice(0, 6);
  const maxHeat = sorted[0].heat;

  return (
    <section className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-10">
        <div className="flex items-end justify-between gap-4 mb-8 md:mb-10">
          <div>
            <p className="text-xs md:text-sm font-medium text-primary-600 tracking-widest mb-2">TODAY / 温度榜</p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">今日瓜田温度榜</h2>
            <p className="mt-2 text-sm text-foreground-600 max-w-2xl">
              按讨论热度实时排序，越靠前说明正在被越多人关注。点开即可看到评论区的主流情绪与不同立场。
            </p>
          </div>
          <Link
            to="/topics"
            className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
          >
            查看完整榜单 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          <article className="lg:col-span-5 bg-secondary-900 rounded-lg overflow-hidden">
            <Link to={`/topic/${featured.id}`} className="block cursor-pointer group">
              <div className="relative w-full h-52 md:h-64 overflow-hidden">
                <img
                  src={featured.cover}
                  alt={`${featured.title} 讨论焦点配图`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <span className="absolute top-4 left-4 px-2.5 py-1 rounded-md bg-accent-500 text-foreground-950 text-xs font-bold">
                  小编在追
                </span>
              </div>
              <div className="p-5 md:p-6">
                <span className="text-xs text-secondary-200">{categoryName(featured.category)}</span>
                <h3 className="mt-2 font-heading text-xl md:text-2xl font-bold text-background-50 leading-snug group-hover:text-accent-400 transition-colors">
                  {featured.title}
                </h3>
                <p className="mt-3 text-sm text-background-200/80 leading-relaxed line-clamp-3">{featured.summary}</p>
                <div className="mt-5">
                  <StanceBar
                    support={featured.stance.support}
                    oppose={featured.stance.oppose}
                    neutral={featured.stance.neutral}
                    compact
                  />
                </div>
                <div className="mt-5 flex items-center gap-5 text-xs text-background-200/70">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-fire-line text-accent-400"></i>热度 {formatHeat(featured.heat)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-discuss-line"></i>
                    {featured.participants.toLocaleString()} 人参与
                  </span>
                </div>
              </div>
            </Link>
          </article>

          <div className="lg:col-span-7">
            <ol className="divide-y divide-background-200/70 border-t border-b border-background-200/70">
              {rest.map((topic, index) => (
                <li key={topic.id}>
                  <Link
                    to={`/topic/${topic.id}`}
                    className="group flex items-center gap-4 py-4 md:py-5 cursor-pointer"
                  >
                    <span
                      className={`w-8 shrink-0 text-center font-heading text-lg md:text-xl font-bold ${
                        index < 3 ? 'text-primary-500' : 'text-foreground-400'
                      }`}
                    >
                      {String(index + 2).padStart(2, '0')}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 truncate group-hover:text-primary-600 transition-colors">
                        {topic.title}
                      </h3>
                      <div className="mt-2 flex items-center gap-3">
                        <div className="flex-1 h-1.5 rounded-full bg-background-200 overflow-hidden">
                          <span
                            className="block h-full rounded-full bg-accent-500"
                            style={{ width: `${Math.round((topic.heat / maxHeat) * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{formatHeat(topic.heat)}</span>
                      </div>
                    </div>
                    <i className="ri-arrow-right-up-line text-foreground-300 group-hover:text-primary-500 transition-colors text-lg"></i>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}